import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Users, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FormModal, Field, SectionTitle } from "@/pages/InwardStock";
import { useCustomFields, CustomFieldsRenderer, CustomDataCell } from "@/components/CustomFields";

const emptyForm = {
  name: "",
  customer_type: "Retail",
  contact_person: "",
  phone: "",
  email: "",
  address: "",
  gst_number: "",
  custom_data: {},
};

const CUSTOMER_TYPES = ["Retail", "Shop", "Dealer", "Distributor"];

export default function Customers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { fields: customFields } = useCustomFields("customer");

  const load = async () => setItems((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.patch(`/customers/${editingId}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added");
      }
      closeForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "",
      customer_type: c.customer_type || "Retail",
      contact_person: c.contact_person || "",
      phone: c.phone || "",
      email: c.email || "",
      address: c.address || "",
      gst_number: c.gst_number || "",
      custom_data: c.custom_data || {},
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const typeBadgeClass = (t) => {
    switch (t) {
      case "Distributor": return "bg-[#16A34A]/10 border-[#16A34A] text-[#15803D]";
      case "Dealer": return "bg-[#0F766E]/10 border-[#0F766E] text-[#0F766E]";
      case "Shop": return "bg-[#D97706]/10 border-[#D97706] text-[#B45309]";
      default: return "bg-zinc-100 border-zinc-300 text-zinc-700";
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#16A34A] mr-2 align-middle" />Buyers</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Customers</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-customer-btn"><Plus className="w-4 h-4" /> New Customer</button>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="data-table" data-testid="customers-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Contact</th>
              <th>Phone</th>
              <th>Email</th>
              <th>GST</th>
              <th>Address</th>
              {customFields.length > 0 && <th>Custom</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={customFields.length > 0 ? 9 : 8} className="text-center py-12 text-zinc-500 font-sans"><Users className="w-10 h-10 mx-auto mb-3 opacity-40" />No customers yet.</td></tr>
            )}
            {items.map((v) => (
              <tr key={v.id}>
                <td className="font-sans font-semibold text-zinc-900">{v.name}</td>
                <td>
                  <span className={`text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5 ${typeBadgeClass(v.customer_type || "Retail")}`} data-testid={`customer-type-${v.id}`}>
                    {v.customer_type || "Retail"}
                  </span>
                </td>
                <td className="font-sans">{v.contact_person || "—"}</td>
                <td>{v.phone || "—"}</td>
                <td className="font-sans">{v.email || "—"}</td>
                <td>{v.gst_number || "—"}</td>
                <td className="font-sans text-zinc-500">{v.address || "—"}</td>
                {customFields.length > 0 && <td><CustomDataCell fields={customFields} data={v.custom_data} /></td>}
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(v)} className="text-zinc-400 hover:text-[#16A34A] transition-colors" data-testid={`edit-customer-${v.id}`} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {user?.role === "admin" && (
                      <button onClick={() => remove(v.id)} className="text-zinc-400 hover:text-[#DC2626] transition-colors" data-testid={`del-customer-${v.id}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <FormModal onClose={closeForm}>
          <form onSubmit={submit} className="space-y-5" data-testid="customer-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">{editingId ? "Edit Customer" : "New Customer"}</h2>
              <button type="button" onClick={closeForm} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <SectionTitle>Customer Details</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name*"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" data-testid="c-name" /></Field>
              <Field label="Customer Type*">
                <select required value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} className="input-field" data-testid="c-type">
                  {CUSTOMER_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Contact Person"><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="input-field" data-testid="c-contact" /></Field>
              <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-field font-mono" data-testid="c-phone" /></Field>
              <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-field" data-testid="c-email" /></Field>
              <Field label="GST Number"><input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className="input-field font-mono" data-testid="c-gst" /></Field>
              <Field label="Address" full><textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-field h-20 resize-none" data-testid="c-address" /></Field>
            </div>

            {customFields.length > 0 && (
              <>
                <SectionTitle>Custom Fields</SectionTitle>
                <div className="grid grid-cols-2 gap-4">
                  <CustomFieldsRenderer
                    fields={customFields}
                    values={form.custom_data}
                    onChange={(cd) => setForm({ ...form, custom_data: cd })}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={closeForm} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-customer">{editingId ? "Update Customer" : "Add Customer"}</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}
